import { Workflow } from "domain/entities/workflow";
import { WorkflowInputFilter } from "domain/models/workflow-input-filter";
import { useWorkflowByFilter } from "presentation/hooks/use-query-workflow";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { MessageTypeEnum } from "domain/enum/messageType";
import TaskToApprovalListSkeleton from "./Skeleton";
import MobileTaskApprovalForm from "../MobileTaskApprovalForm";
import useLongPress from "presentation/mobile/hooks/useLongPress";
import MobileFilterTabs, { FilterTabItem } from "presentation/mobile/components/MobileFilterTabs";
import MobileActionButton from "presentation/mobile/components/MobileActionButton";
import { useVacationPlanById } from "presentation/hooks/use-query-vacation";
import MobileToast from "../../MobileToast";

interface TaskToApprovalListProps {
    level: number;
}

interface Task {
    id: string;
    type: string;
    description: string;
    dateRef?: Date;
    status: string;
}

const getDocumentIcon = (type: string) => {
    const lower = type.toLowerCase();
    if (lower.includes("férias") || lower.includes("ferias")) return "pi pi-calendar";
    if (lower.includes("despesa")) return "pi pi-euro";
    if (lower.includes("hora") || lower.includes("timesheet")) return "pi pi-clock";
    if (lower.includes("km") || lower.includes("viagem") || lower.includes("trip")) return "pi pi-map";
    return "pi pi-file";
};

const getBadgeInfo = (level: number) => {
    if (level === 1) {
        return {
            label: "A Aprovar",
            color: "#D97706",
            bg: "#FFFBEB",
            border: "#FDE68A",
        };
    }
    return {
        label: "A Assinar",
        color: "#0084D1",
        bg: "#EFF6FF",
        border: "#BFDBFE",
    };
};

interface TaskItemProps {
    task: Workflow;
    level: number;
    isExpanded: boolean;
    onToggle: () => void;
    onAction: (task: Workflow) => void;
}

const TaskItem: React.FC<TaskItemProps> = ({
    task,
    level,
    isExpanded,
    onToggle,
    onAction,
}) => {
    const { isPressing, handlers } = useLongPress({
        onLongPress: onToggle,
    });

    const isVacation = Boolean(task.type && (task.type.toLowerCase().includes("férias") || task.type.toLowerCase().includes("ferias")));

    const { data: vacationData } = useVacationPlanById(isVacation && task.id ? task.id : undefined);
    const daysNextYear = vacationData?.data?.daysNextYear ?? 0;

    const badge = getBadgeInfo(level);
    const icon = getDocumentIcon(task.type);

    const formatDate = (date?: Date | string | null) => {
        if (!date) return null;
        return new Date(date).toLocaleDateString("pt-PT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const actionLabel = level === 1 ? "Validar" : "Assinar";
    const actionIcon = level === 1 ? "pi pi-check-circle" : "pi pi-file-edit";

    return (
        <div
            {...handlers}
            className="border-round-xl p-3 flex flex-column gap-2 cursor-pointer"
            style={{
                backgroundColor: "#FFFFFF",
                border: isExpanded ? "1.5px solid #0084D1" : "1px solid #E2E8F0",
                boxShadow: isExpanded ? "0 4px 14px rgba(0, 132, 209, 0.08)" : "none",
                transform: isPressing ? "scale(0.99)" : "scale(1)",
                transition: "all 0.2s ease",
            }}
        >
            {/* Linha 1: Ícone + Tipo de Documento + Badge de Estado + Botão '+' / '✕' */}
            <div className="w-full flex justify-content-between align-items-center">
                <div className="flex align-items-center gap-2 overflow-hidden pr-2">
                    {isExpanded && (
                        <span
                            style={{
                                color: "#0084D1",
                                fontSize: "1.1rem",
                                lineHeight: "1",
                                marginRight: "2px",
                            }}
                        >
                            •
                        </span>
                    )}
                    <i className={`${icon} text-sm`} style={{ color: "#0084D1" }} />
                    <span className="font-semibold text-sm text-900 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                        {task.type}
                    </span>
                </div>

                <div className="flex align-items-center gap-2 flex-shrink-0">
                    <span
                        className="badge-squircle"
                        style={{
                            color: badge.color,
                            backgroundColor: badge.bg,
                            border: `1px solid ${badge.border}`,
                        }}
                    >
                        {badge.label}
                    </span>

                    {/* Micro-interação: '+' gira 45º para '✕' */}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggle();
                        }}
                        className="border-none bg-transparent cursor-pointer flex align-items-center justify-content-center p-0"
                        style={{
                            width: "24px",
                            height: "24px",
                            color: "#94A3B8",
                            outline: "none",
                        }}
                        aria-label={isExpanded ? "Recolher" : "Expandir"}
                    >
                        <i
                            className="pi pi-plus text-xs"
                            style={{
                                transform: isExpanded ? "rotate(45deg)" : "rotate(0deg)",
                                transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            }}
                        />
                    </button>
                </div>
            </div>

            {/* Linha 2: Descrição (ex: Colaborador / Período) */}
            <div className="w-full">
                <span className="text-xs text-700 font-medium line-height-2">
                    {task.description}
                </span>
            </div>

            {/* Linha 3: Data de Criação (se existir) */}
            {task.createdAt && (
                <div className="w-full">
                    <span className="text-xs text-500">
                        Submetido a: <strong style={{ color: "#334155" }}>{formatDate(task.createdAt)}</strong>
                    </span>
                </div>
            )}
            {/*Aviso caso utilize dias do ano seguinte*/}
            {daysNextYear > 0 && (
                <div className="w-full flex align-items-center gap-2 px-2 py-1 border-round-md"
                    style={{
                        backgroundColor: "#FFFBEB",
                        border: "1px solid #FDE68A",
                    }}>
                    <i className="pi pi-exclamation-triangle text-sm" style={{ color: "#D97706" }} />
                    <span className="text-xs font-medium" style={{ color: "#92400E" }}>
                        Utiliza {daysNextYear} {daysNextYear === 1 ? "dia" : "dias"} de férias do ano seguinte.
                    </span>
                </div>
            )}

            {/* Secção Expandida: Botão Reutilizável de Ação */}
            {isExpanded && (
                <div
                    className="w-full flex flex-column gap-2 pt-2 mt-1"
                    style={{ borderTop: "1px solid #F1F5F9" }}
                >
                    <div className="w-full flex justify-content-end gap-2 pt-1">
                        <MobileActionButton
                            label={actionLabel}
                            icon={actionIcon}
                            severity="primary"
                            onClick={() => onAction(task)}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const TaskToApprovalList = ({ level }: TaskToApprovalListProps) => {
    const { userEmployee } = useAuthStore();
    const [documentKey, setDocumentKey] = useState<Task>();
    const [showModal, setShowModal] = useState(false);
    const [tasks, setTasks] = useState<Workflow[]>([]);
    const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<string>("all");
    const toast = useRef<Toast>(null);

    const showMessage = useCallback(
        (type: MessageTypeEnum, subject: string, message: string) => {
            toast.current?.show({
                severity: type,
                summary: subject,
                detail: message,
                life: 3500,
            });
        },
        []
    );


    const {
        data: tasksToApproval,
        refetch: refetchWorkflowToApproval,
        isLoading: isLoadingTasks,
        isError,
    } = useWorkflowByFilter({
        employeeId: userEmployee?.id,
        level: level,
    } as WorkflowInputFilter);

    useEffect(() => {
        if (!tasksToApproval?.data || isLoadingTasks) return;
        setTasks(tasksToApproval.data);
    }, [tasksToApproval?.data, isLoadingTasks]);

    const handleVacationReport = useCallback(async (Task: Task) => {
        const regex = /(\d{2}\/\d{2}\/\d{4})/g;
        const dates = Task.description.match(regex);
        if (dates && dates.length > 0) {
            const partialDate = dates[0].split("/");
            Task.dateRef = new Date(
                parseInt(partialDate[2]),
                parseInt(partialDate[1]) - 1,
                parseInt(partialDate[0])
            );
        }
        setDocumentKey(Task);
        setShowModal(true);
    }, []);

    const handleGenerateReport = useCallback(async (task: Task) => {
        setDocumentKey(task);
        setShowModal(true);
    }, []);

    const handleCloseModal = useCallback(async () => {
        setShowModal(false);
    }, []);

    const handleCloseModalAfterAction = useCallback(async () => {
        await refetchWorkflowToApproval();
        setShowModal(false);
    }, [refetchWorkflowToApproval]);

    const generateReport = (taskToApprove: Workflow) => {
        if (!taskToApprove.id) return;

        const task: Omit<Task, "employeeId"> = {
            id: taskToApprove.id,
            type: taskToApprove.type,
            description: taskToApprove.description,
            status: taskToApprove.status,
            dateRef: taskToApprove.createdAt,
        };

        if (taskToApprove.type.includes("Férias")) {
            handleVacationReport(task);
            return;
        }

        handleGenerateReport(task);
    };

    // Abas de filtro dinâmicas por Tipo de Documento
    const filterTabs: FilterTabItem<string>[] = useMemo(() => {
        const typeCounts = new Map<string, number>();
        tasks.forEach((t) => {
            const key = t.type || "Outros";
            typeCounts.set(key, (typeCounts.get(key) ?? 0) + 1);
        });

        const tabs: FilterTabItem<string>[] = [
            { key: "all", label: "Todos", count: tasks.length },
        ];

        typeCounts.forEach((count, type) => {
            tabs.push({ key: type, label: type, count });
        });

        return tabs;
    }, [tasks]);

    // Filtragem da lista
    const filteredTasks = useMemo(() => {
        if (activeTab === "all") return tasks;
        return tasks.filter((t) => (t.type || "Outros") === activeTab);
    }, [tasks, activeTab]);

    if (!tasksToApproval?.data || isLoadingTasks) {
        return <TaskToApprovalListSkeleton />;
    }

    return (
        <div className="flex flex-column pt-2 px-3 gap-2 pb-8 align-items-center">
            {/* Abas de Filtro Reutilizáveis (apenas se houver mais do que 1 tipo de documento) */}
            {filterTabs.length > 2 && (
                <MobileFilterTabs
                    tabs={filterTabs}
                    activeTab={activeTab}
                    onChange={setActiveTab}
                    scrollable
                />
            )}

            {/* Cabeçalho da Secção (Sem CAPSLOCK) */}
            <div className="w-full flex justify-content-between align-items-center pt-1 px-1">
                <span
                    className="font-semibold text-xs"
                    style={{ color: "#64748B" }}
                >
                    {level === 1 ? "Documentos para aprovar" : "Documentos para assinar"}
                </span>
                <span className="text-xs" style={{ color: "#94A3B8" }}>
                    {filteredTasks.length} {filteredTasks.length === 1 ? "documento" : "documentos"}
                </span>
            </div>

            {/* Lista de Documentos */}
            <div className="w-full flex flex-column justify-content-center gap-3">
                {isError ? (
                    <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                        <i className="pi pi-exclamation-circle text-4xl text-red-500" />
                        <span className="text-sm text-center text-600">
                            Não foi possível carregar os documentos.
                        </span>
                    </div>
                ) : filteredTasks.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center pt-6 gap-2">
                        <i className="pi pi-file text-4xl" style={{ color: "#CBD5E1" }} />
                        <span className="text-sm text-center" style={{ color: "#64748B" }}>
                            {level === 1
                                ? "Não tem documentos pendentes de aprovação."
                                : "Não tem documentos pendentes de assinatura."}
                        </span>
                    </div>
                ) : (
                    filteredTasks.map((task) => (
                        <TaskItem
                            key={task.id}
                            task={task}
                            level={level}
                            isExpanded={expandedTaskId === task.id}
                            onToggle={() =>
                                setExpandedTaskId((prev) =>
                                    prev === task.id ? null : task.id ?? null
                                )
                            }
                            onAction={generateReport}
                        />
                    ))
                )}
            </div>

            {/* Modal de Validação / Assinatura */}
            <Dialog
                header={`${level === 1 ? "Aprovar" : "Assinar"} Documento: ${documentKey?.type ?? ""}`}
                visible={showModal}
                style={{ width: "95vw", height: "92vh", margin: "auto" }}
                onHide={handleCloseModal}
                maximized
                className="flex flex-column justify-content-center"
                contentStyle={{ backgroundColor: "#F8FAFC" }}
            >
                <MobileTaskApprovalForm
                    document={documentKey}
                    onClose={handleCloseModalAfterAction}
                    showMessage={showMessage}
                />
            </Dialog>
            <MobileToast ref={toast} />
        </div>
    );
};

export default TaskToApprovalList;